import React from "react";
import PropTypes from 'prop-types';
import parseISO from "date-fns/parseISO";
import differenceInMinutes from "date-fns/differenceInMinutes";
import formatDistanceToNowStrict from "date-fns/formatDistanceToNowStrict";
import differenceInSeconds from "date-fns/differenceInSeconds";
import differenceInDays from "date-fns/differenceInDays";
import { enGB, de } from 'date-fns/locale'
import { Text, renderHTML } from "~/native-ui"
import { connect } from "~/redux/connect";

const locale = {
    en: enGB,
    de
}

const secondsToMS = (seconds) => seconds * 1000;
const minutesToMS = (minutes) => secondsToMS(minutes) * 60;
const hoursToMS = (hours) => minutesToMS(hours) * 60;

/**
 * @typedef IntervalObject
 * @type {object}
 * @property {number} hours - hour interval.
 * @property {number} minutes - minute interval.
 * @property {number} seconds - second interval.
 */


/**
    * @typedef IntervalEnum
    * @property {string} Hour "hour"
    * @property {string} Minute "minute"
    * @property {string} Second "second"
*/



/**
 * 
 * @param {(number|IntervalObject|IntervalEnum)} interval 
 * @returns number
 */
const normalizeInterval = (interval, date) => {
    if (date && Math.abs(differenceInSeconds(date, new Date) < 120)) {
        interval = "second"
    }

    if (typeof interval === "number" && interval) {
        return interval;
    }
    
    if (typeof interval === "object") {
        const { hours = 0, minutes = 0, seconds = 0 } = interval;
        return hoursToMS(hours) + minutesToMS(minutes) + secondsToMS(seconds)
    }

    if (interval === "second") {
        return secondsToMS(1)
    }

    if (interval === "hour") {
        return hoursToMS(1)
    }

    return minutesToMS(1);
}





const getRelativeDate = (date, interval, trans) => {
    let unit = "second"
  
    if (Math.abs(differenceInSeconds(new Date, date)) > 60) {
        unit = "minute"
    }

    if (Math.abs(differenceInMinutes(new Date, date)) > 60) {
        unit = "hour"
    }

    if (Math.abs(differenceInDays(new Date, date)) > 1) {
        unit = "day"
    }

    return formatDistanceToNowStrict(date, { addSuffix: true, roundingMethod: "ceil", unit, locale: locale[trans("locale")] || locale.en })
}


function normalizeDate (date) {
    return (date ? parseISO(typeof date !== "string" || date.endsWith("Z") ? date : (date + "Z")) : new Date).getTime();
}


const getReferenceDate = (props) => {
    const date = props.date,
          dueText = props.dueText || null,
          isPastDue = differenceInSeconds(normalizeDate(date), new Date) < 1

    return { date: normalizeDate(date), isPastDue, dueText };
}





const useCounter = (props) => {
    const unmounted = React.useRef(false);
    const intervalTimer = React.useRef(null);
    const trans = React.useRef(props.trans).current;
    const { _text } = props;
    const { date: referenceDate, isPastDue, dueText } = getReferenceDate(props);
    const interval = normalizeInterval(props.interval, referenceDate);
    const [ date, setCounter ] = React.useState(getRelativeDate(referenceDate, interval, trans));


    React.useEffect(
        () => {
            
            unmounted.current = false;
            setCounter(getRelativeDate(referenceDate, interval, trans));
            clearInterval(intervalTimer.current);

            if (dueText && isPastDue) {
                intervalTimer.current = null;    
                return;
            }

            intervalTimer.current = setInterval(() => {
                if (unmounted.current) {
                    clearInterval(intervalTimer.current);
                    return;
                }

                setCounter(getRelativeDate(referenceDate, interval, trans))
            }, interval);

            return;
        },
        [unmounted, referenceDate, interval, dueText, isPastDue]
    );

    React.useEffect(() => () => {
        unmounted.current = true;
        clearInterval(intervalTimer.current);
        intervalTimer.current = null;
    }, [])

    if (isPastDue && dueText) {
        return <Text { ..._text }>{ renderHTML(dueText) }</Text>
    }

    return <Text  { ..._text } data-highlight={ isPastDue ? "error" : "" }>{ date }</Text>;
}





export const TimeCounter = React.memo(connect()((props) => {
    const date = useCounter(props);
    return <React.Fragment>{ date }</React.Fragment>
}));


TimeCounter.propTypes = {
    dueText: PropTypes.string,
    interval: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf(["hour", "minute", "second"]),
        PropTypes.shape({
            hours: PropTypes.number,
            minutes: PropTypes.number,
            seconds: PropTypes.number,
        })
    ])
}

TimeCounter.defaultProps = {
    interval: "second"
}